/**
 * DataProvider tag store.
 *
 * Tracks the dependency map, cache-tag checksums, and prop manifest
 * emitted by DataProvider responses. Used for two related optimizations:
 *
 * 1. Auto-partial reloads. When URL params change on the same route,
 *    the store computes which prop keys are stale (their dependsOn
 *    list intersects the changed params) and passes only those keys
 *    to the server via the X-SDX-Stale header. The server then
 *    re-evaluates only the stale props, leaving everything else
 *    untouched.
 *
 * 2. Tag-based freshness headers. The X-SDX-Tags header forwards
 *    each cache tag alongside its client-side checksum. The server
 *    compares checksums, skips tags whose state is still fresh, and
 *    returns only the diff.
 *
 * The store is cleared on any non-content-scope navigation: the new
 * route has different props with different dependencies, so stale
 * checksums from the previous route must not leak across boundaries.
 */

import type { PartialResponseEnvelope } from './types';

export class TagStore {
  private dependencyMap: Record<string, string[]> = {};
  private tagChecksums: Record<string, string> = {};
  private manifest: Record<string, unknown> = {};
  private component = '';

  hasComponent(): boolean {
    return this.component !== '';
  }

  update(envelope: PartialResponseEnvelope): void {
    if (envelope._dependencyMap) this.dependencyMap = envelope._dependencyMap;
    if (envelope._tagChecksums) this.tagChecksums = envelope._tagChecksums;
    if (envelope._manifest) this.manifest = envelope._manifest;
    if (envelope.component) this.component = envelope.component;
  }

  clear(): void {
    this.dependencyMap = {};
    this.tagChecksums = {};
    this.manifest = {};
    this.component = '';
  }

  /**
   * Returns the prop keys whose declared dependsOn list intersects
   * the query parameters that changed between `oldUrl` and `newUrl`.
   * Empty when there is no component state or no changed params.
   */
  getStaleKeys(oldUrl: string, newUrl: string): string[] {
    let oldParams: URLSearchParams;
    let newParams: URLSearchParams;
    try {
      oldParams = new URL(oldUrl, location.origin).searchParams;
      newParams = new URL(newUrl, location.origin).searchParams;
    }
    catch {
      return [];
    }

    const changed = new Set<string>();
    newParams.forEach((val, key) => {
      if (oldParams.get(key) !== val) changed.add(key);
    });
    oldParams.forEach((_val, key) => {
      if (!newParams.has(key)) changed.add(key);
    });
    if (changed.size === 0) return [];

    const stale: string[] = [];
    for (const [propKey, deps] of Object.entries(this.dependencyMap)) {
      if (deps.some((dep) => changed.has(dep))) {
        stale.push(propKey);
      }
    }
    return stale;
  }

  /**
   * Returns the X-SDX-Tags header value - `tag:checksum`
   * comma-separated - or an empty string when the store is empty.
   */
  getTagHeader(): string {
    const parts: string[] = [];
    for (const [tag, checksum] of Object.entries(this.tagChecksums)) {
      parts.push(`${tag}:${checksum}`);
    }
    return parts.join(',');
  }
}
